/**
 * useVideoFeed - loads a channel's videos.
 *
 * Fetches `GET {API_BASE}/content/{contentId}` and normalises the response
 * into `{ feed, videos }`, hydrating each video with its persisted user
 * reactions and a seeded reaction count when the API omits one.
 *
 * If the live API is unreachable (CORS / outside the native shell), it falls
 * back to the bundled demo feed so the UI stays fully interactive.
 */
import { useState, useEffect } from 'preact/hooks';
import { API_BASE, parseFeedConfig, seedReactions } from '../lib/content.js';
import { loadUserReactions } from '../lib/reactions.js';
import { buildMockFeed } from '../lib/mockFeed.js';

const EMPTY_FEED = { cdnUrl: '', cdnPath: '', imageBase: '' };

export function useVideoFeed(contentId, { onContentReset } = {}) {
  const [feed, setFeed] = useState(EMPTY_FEED);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [usingMock, setUsingMock] = useState(false);
  const [allUserReactions, setAllUserReactions] = useState({});

  useEffect(() => {
    let cancelled = false;

    function hydrate(items, reactionsByVideo) {
      return items.map((v) => ({
        ...v,
        reactions: v.reactions || seedReactions(),
        userReactions: reactionsByVideo[v.id] || {},
      }));
    }

    function applyMock(reactionsByVideo) {
      const mock = buildMockFeed();
      setUsingMock(true);
      setFeed(mock.feed);
      setVideos(hydrate(mock.items, reactionsByVideo));
    }

    async function load() {
      onContentReset?.();
      setLoading(true);
      setLoadError('');
      setUsingMock(false);
      setVideos([]);

      const reactionsByVideo = loadUserReactions();
      setAllUserReactions(reactionsByVideo);

      try {
        const url = new URL(`/content/${encodeURIComponent(contentId)}`, API_BASE);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to load video API: HTTP ${res.status}`);
        const json = await res.json();
        const items = Array.isArray(json.items) ? json.items : [];
        if (cancelled) return;
        if (!items.length) throw new Error('Empty feed');
        setFeed(parseFeedConfig(json));
        setVideos(hydrate(items, reactionsByVideo));
      } catch (err) {
        if (cancelled) return;
        console.warn('Live feed unavailable, using demo feed:', err);
        applyMock(reactionsByVideo);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [contentId]);

  return {
    videos,
    setVideos,
    feed,
    loading,
    loadError,
    usingMock,
    allUserReactions,
    setAllUserReactions,
  };
}
